import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, DIMENSION_META } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight } from "lucide-react";

const LIKERT = [
  { v: 1, label: "Strongly disagree" },
  { v: 2, label: "Disagree" },
  { v: 3, label: "Neutral" },
  { v: 4, label: "Agree" },
  { v: 5, label: "Strongly agree" },
];

export default function Diagnostic() {
  const nav = useNavigate();
  const [qs, setQs] = useState([]);
  const [i, setI] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get("/diagnostic/questions").then((r) => setQs(r.data.questions)); }, []);

  if (!qs.length) return <div className="p-16 text-center text-slate-500">Loading…</div>;

  const q = qs[i];
  const meta = DIMENSION_META[q.dimension];
  const done = i === qs.length - 1;
  const canNext = answers[q.id];

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/diagnostic/submit", { answers });
      toast.success("Baseline saved");
      nav("/app/diagnostic/results");
    } catch (e) {
      toast.error("Submission failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-12 fade-up">
      <button onClick={() => nav("/app")} data-testid="diag-back" className="text-sm text-slate-500 hover:text-[#0B1E36] mb-8 inline-flex items-center gap-1">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <div className="mb-8">
        <div className="flex items-center justify-between text-xs text-slate-500 mb-2">
          <span data-testid="diag-progress-text">Question {i + 1} of {qs.length}</span>
          <span style={{ color: meta.color }} className="font-semibold uppercase tracking-widest">{meta.label}</span>
        </div>
        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full transition-all duration-500" style={{ width: `${((i + 1) / qs.length) * 100}%`, background: meta.color }} />
        </div>
      </div>

      <div className="bg-white p-10 rounded-2xl border border-slate-200 mb-8">
        <div className="text-xs uppercase tracking-widest text-slate-400 mb-4">Statement</div>
        <h2 className="font-serif text-2xl lg:text-3xl text-[#0B1E36] leading-snug" data-testid="diag-question">{q.text}</h2>
        <div className="mt-10 space-y-3">
          {LIKERT.map((opt) => {
            const active = answers[q.id] === opt.v;
            return (
              <button
                key={opt.v} data-testid={`diag-opt-${opt.v}`}
                onClick={() => setAnswers({ ...answers, [q.id]: opt.v })}
                className={`w-full text-left px-5 py-4 rounded-xl border-2 transition-colors flex items-center justify-between ${
                  active ? "border-[#EC4899] bg-[#EC4899]/5" : "border-slate-200 hover:border-slate-300"
                }`}>
                <span className="font-medium text-[#0B1E36]">{opt.label}</span>
                <div className={`h-5 w-5 rounded-full border-2 grid place-items-center ${
                  active ? "border-[#EC4899] bg-[#EC4899]" : "border-slate-300"
                }`}>
                  {active && <div className="h-1.5 w-1.5 bg-white rounded-full" />}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <button
          disabled={i === 0} onClick={() => setI(i - 1)} data-testid="diag-prev"
          className="px-5 py-2.5 text-sm font-semibold text-slate-600 disabled:opacity-30 disabled:cursor-not-allowed hover:text-[#0B1E36]">
          ← Previous
        </button>
        {done ? (
          <button
            disabled={!canNext || submitting} onClick={submit} data-testid="diag-submit"
            className="bg-[#EC4899] text-white px-8 py-3 rounded-full font-semibold btn-lift disabled:opacity-50 hover:bg-[#DB2777] shadow-[0_8px_24px_rgba(236,72,153,0.25)]">
            {submitting ? "Generating your plan…" : "Get my results"}
          </button>
        ) : (
          <button
            disabled={!canNext} onClick={() => setI(i + 1)} data-testid="diag-next"
            className="bg-[#0B1E36] text-white px-6 py-3 rounded-full font-semibold btn-lift disabled:opacity-40 hover:bg-[#132a4a] inline-flex items-center gap-2">
            Next <ArrowRight className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}
